import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { trackView } from "@/lib/trackView";
import { ShoppingBag, Store, MapPin, Globe, Phone, Mail } from "lucide-react";

export default function MarketplaceStore() {
  const { sellerId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    trackView("marketplace", sellerId);
    api.get(`/marketplace-sellers/${sellerId}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [sellerId]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="seller-not-found">
        <h1 className="text-2xl font-black text-forest mb-2">Seller not found</h1>
        <Link to="/resources?tab=directory" className="text-terracotta-dark font-bold hover:underline">Back to the marketplace</Link>
      </div>
    );
  }
  if (!data) return <div className="text-center py-20 text-forest/50">Loading...</div>;

  const { seller, listings } = data;
  const socials = [seller.social_link_1, seller.social_link_2, seller.social_link_3].filter(Boolean);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="marketplace-store-page">
      <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 shadow-sm mb-8">
        <div className="flex items-start gap-5">
          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl overflow-hidden bg-sage-light border border-sage/20 flex items-center justify-center shrink-0" data-testid="store-logo">
            {seller.business_logo ? (
              <img src={fileUrl(seller.business_logo)} alt={seller.business_name} className="w-full h-full object-contain" />
            ) : (
              <img src="/logo.png" alt="Compass Kids logo" className="w-full h-full object-contain p-2 opacity-85" data-testid="store-logo-fallback" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-black text-forest leading-tight" data-testid="store-name">{seller.business_name}</h1>
            {seller.tagline && <p className="text-sm text-forest/70 mt-1" data-testid="store-tagline">{seller.tagline}</p>}
            {(seller.product_categories || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3" data-testid="store-categories">
                {seller.product_categories.map((c) => (
                  <span key={c} className="text-[10px] font-bold uppercase tracking-wide bg-sage-light text-sage-dark px-2 py-0.5 rounded-full">{c}</span>
                ))}
              </div>
            )}
          </div>
        </div>

        {seller.description && (
          <p className="text-forest/75 leading-relaxed mt-6 whitespace-pre-line" data-testid="store-description">{seller.description}</p>
        )}

        <div className="mt-6 pt-5 border-t border-sage/15 grid sm:grid-cols-2 gap-4 text-sm">
          {seller.location && (
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(seller.location)}`}
               target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-2 text-sage-dark hover:text-terracotta-dark hover:underline"
               data-testid="store-location-link">
              <MapPin className="w-4 h-4" /> {seller.location}
            </a>
          )}
          {seller.website && (
            <a href={seller.website.startsWith("http") ? seller.website : `https://${seller.website}`}
               target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-2 text-sage-dark hover:text-terracotta-dark hover:underline"
               data-testid="store-website-link">
              <Globe className="w-4 h-4" /> {seller.website}
            </a>
          )}
          {seller.phone && (
            <span className="flex items-center gap-2 text-forest/70" data-testid="store-phone">
              <Phone className="w-4 h-4 text-sage-dark" /> {seller.phone}
            </span>
          )}
          {seller.contact_email && (
            <a href={`mailto:${seller.contact_email}`} className="flex items-center gap-2 text-sage-dark hover:text-terracotta-dark hover:underline" data-testid="store-email-link">
              <Mail className="w-4 h-4" /> {seller.contact_email}
            </a>
          )}
        </div>

        {socials.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2 text-xs" data-testid="store-socials">
            {socials.map((s, i) => (
              <span key={i} className="bg-cream text-forest/70 border border-sage/20 rounded-full px-3 py-1">{s}</span>
            ))}
          </div>
        )}
      </div>

      <h2 className="font-heading font-bold text-forest text-xl mb-4" data-testid="store-listings-heading">
        Products from {seller.business_name} <span className="text-forest/50 font-semibold">({listings.length})</span>
      </h2>

      {listings.length === 0 ? (
        <div className="text-center py-16 bg-white/60 border border-sage/20 rounded-3xl" data-testid="store-empty">
          <ShoppingBag className="w-10 h-10 text-sage mx-auto mb-3" />
          <p className="text-forest/60">This seller hasn't listed any products yet.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {listings.map((item) => (
            <Link key={item.listing_id} to={`/marketplace/${item.listing_id}`}
              className="group relative bg-white/80 border border-sage/20 rounded-3xl overflow-hidden shadow-sm hover:shadow-md hover:scale-[1.02] transition-[transform,box-shadow]"
              data-testid={`store-item-${item.listing_id}`}>
              <div className="h-44 bg-sage-light flex items-center justify-center overflow-hidden">
                {item.photos?.length > 0 ? (
                  <img src={fileUrl(item.photos[0])} alt={item.product_name} className="w-full h-full object-contain" />
                ) : (
                  <img src="/logo.png" alt="Compass Kids logo" className="max-h-24 max-w-[70%] object-contain opacity-80" data-testid={`store-item-photo-fallback-${item.listing_id}`} />
                )}
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-heading font-bold text-forest leading-snug">{item.product_name}</h3>
                  {item.price && (
                    <span className="shrink-0 text-sm font-black text-terracotta-dark bg-terracotta-light px-2.5 py-1 rounded-full" data-testid={`store-item-price-${item.listing_id}`}>{item.price}</span>
                  )}
                </div>
                <span className="inline-block text-[10px] font-bold uppercase tracking-wide bg-sage-light text-sage-dark px-2 py-0.5 rounded-full mt-3">{item.category}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
